import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoOrthographic, geoPath, geoGraticule10 } from "d3-geo";
import { feature } from "topojson-client";
import land from "world-atlas/land-110m.json";
import { Globe2, Map as MapIcon, Square, Pentagon, RotateCcw, Hand, Plus, X, ZoomIn, ZoomOut, Crosshair, MapPin as PinIcon, Pencil, Check } from "lucide-react";
import { polygonAreaKm2, rectBboxCoords, bboxFromCoords } from "@/lib/geo";

const WIDTH = 820;
const HEIGHT = 560;

const LAND = feature(land, land.objects.land);
const GRAT = geoGraticule10();

const AOI_COLORS = ["#00E5FF", "#22C55E", "#F59E0B", "#8B5CF6", "#FF7A1A", "#EC4899"];

export default function AOISelector({ aois, setAois, initialView = "globe", focusTarget, pin, onPinDrop }) {
  const [view, setView] = useState(initialView);
  const [mode, setMode] = useState("rectangle");
  const [rotation, setRotation] = useState([-20, -20]);
  const [zoom, setZoom] = useState(1);
  const [pan2d, setPan2d] = useState([0, 0]);
  const [draftPoints, setDraftPoints] = useState([]); // currently drawing polygon vertices
  const [rectStart, setRectStart] = useState(null);
  const [rectPreview, setRectPreview] = useState(null); // [sw,ne]
  const [hoverLL, setHoverLL] = useState(null);
  const [dragging, setDragging] = useState(null);
  const svgRef = useRef(null);

  // Imperative "fly to" when a preset or shared AOI is loaded.
  useEffect(() => {
    if (!focusTarget) return;
    const [w, s, e, n] = focusTarget.bbox || [];
    if (!isFinite(w)) return;
    const cx = (w + e) / 2;
    const cy = (s + n) / 2;
    if (view === "globe") {
      setRotation([-cx, -cy]);
    } else {
      setPan2d([0, 0]);
    }
    const span = Math.max(e - w, n - s);
    const nextZoom = Math.max(1, Math.min(12, 70 / Math.max(5, span)));
    setZoom(nextZoom);
  }, [focusTarget, view]);

  const projection = useMemo(() => {
    if (view === "globe") {
      return geoOrthographic()
        .scale((HEIGHT / 2 - 20) * zoom)
        .translate([WIDTH / 2, HEIGHT / 2])
        .rotate(rotation)
        .clipAngle(90);
    }
    return geoMercator()
      .scale(((WIDTH / (2 * Math.PI)) * 0.95) * zoom)
      .translate([WIDTH / 2 + pan2d[0], HEIGHT / 2 + 60 + pan2d[1]]);
  }, [view, rotation, zoom, pan2d]);

  const path = useMemo(() => geoPath(projection), [projection]);

  // Clear drafts when switching mode/view
  useEffect(() => {
    setDraftPoints([]);
    setRectStart(null);
    setRectPreview(null);
  }, [view, mode]);

  const svgToLL = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    const x = ((evt.clientX - r.left) / r.width) * WIDTH;
    const y = ((evt.clientY - r.top) / r.height) * HEIGHT;
    const ll = projection.invert ? projection.invert([x, y]) : null;
    if (!ll || !isFinite(ll[0]) || !isFinite(ll[1])) return null;
    return ll;
  };

  const commitAoi = (coords, m) => {
    const ring = coords[0] === coords[coords.length - 1] ? coords : [...coords, coords[0]];
    const bbox = bboxFromCoords(ring);
    const area_km2 = polygonAreaKm2(ring);
    setAois((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), name: `AOI ${prev.length + 1}`, mode: m, coords: ring, bbox, area_km2 },
    ]);
  };

  const handleDown = (evt) => {
    if (mode === "pan") {
      if (view === "globe") {
        setDragging({ type: "rotate", startX: evt.clientX, startY: evt.clientY, startRot: rotation });
      } else {
        setDragging({ type: "pan2d", startX: evt.clientX, startY: evt.clientY, startPan: pan2d });
      }
      return;
    }
    const ll = svgToLL(evt);
    if (!ll) return;
    if (mode === "rectangle") {
      setRectStart(ll);
      setRectPreview([ll, ll]);
      setDragging({ type: "rect" });
    }
  };

  const handleMove = (evt) => {
    if (dragging?.type === "rotate") {
      const dx = evt.clientX - dragging.startX;
      const dy = evt.clientY - dragging.startY;
      const [l0, p0] = dragging.startRot;
      const sensitivity = 0.4 / Math.max(1, zoom);
      setRotation([l0 + dx * sensitivity, Math.max(-85, Math.min(85, p0 - dy * sensitivity))]);
      return;
    }
    if (dragging?.type === "pan2d") {
      const dx = evt.clientX - dragging.startX;
      const dy = evt.clientY - dragging.startY;
      setPan2d([dragging.startPan[0] + dx, dragging.startPan[1] + dy]);
      return;
    }
    const ll = svgToLL(evt);
    if (ll) setHoverLL(ll);
    if (dragging?.type === "rect" && rectStart && ll) {
      setRectPreview([rectStart, ll]);
    }
  };

  const handleWheel = (evt) => {
    evt.preventDefault();
    const delta = -evt.deltaY;
    const factor = delta > 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => Math.max(0.4, Math.min(40, z * factor)));
  };

  const resetView = () => {
    setZoom(1);
    setPan2d([0, 0]);
    setRotation([-20, -20]);
  };

  const handleUp = () => {
    if (dragging?.type === "rect" && rectStart && rectPreview) {
      const [a, b] = rectPreview;
      if (a[0] !== b[0] && a[1] !== b[1]) {
        const sw = [Math.min(a[0], b[0]), Math.min(a[1], b[1])];
        const ne = [Math.max(a[0], b[0]), Math.max(a[1], b[1])];
        commitAoi(rectBboxCoords(sw, ne), "rectangle");
      }
      setRectStart(null);
      setRectPreview(null);
    }
    setDragging(null);
  };

  const handleClick = (evt) => {
    const ll = svgToLL(evt);
    if (!ll) return;
    if (mode === "pin") {
      onPinDrop?.(ll);
      return;
    }
    if (mode !== "polygon") return;
    setDraftPoints((p) => [...p, ll]);
  };

  const finishPolygon = () => {
    if (draftPoints.length >= 3) {
      commitAoi(draftPoints, "polygon");
    }
    setDraftPoints([]);
  };

  const removeAoi = (id) => setAois((prev) => prev.filter((a) => a.id !== id));
  const clearAll = () => {
    setAois([]);
    setDraftPoints([]);
    setRectStart(null);
    setRectPreview(null);
  };

  // Preview rectangle while dragging
  const previewCoords = useMemo(() => {
    if (mode !== "rectangle" || !rectPreview) return null;
    const [a, b] = rectPreview;
    const sw = [Math.min(a[0], b[0]), Math.min(a[1], b[1])];
    const ne = [Math.max(a[0], b[0]), Math.max(a[1], b[1])];
    return rectBboxCoords(sw, ne);
  }, [rectPreview, mode]);

  const previewPath = useMemo(() => (previewCoords ? path({ type: "Polygon", coordinates: [previewCoords] }) : ""), [previewCoords, path]);

  const draftPath = useMemo(() => {
    if (mode !== "polygon" || draftPoints.length < 2) return "";
    const open = { type: "LineString", coordinates: draftPoints };
    return path(open);
  }, [draftPoints, mode, path]);

  const landPath = useMemo(() => path(LAND), [path]);
  const sphere = useMemo(() => path({ type: "Sphere" }), [path]);
  const grat = useMemo(() => path(GRAT), [path]);

  const cursorStyle =
    dragging?.type === "rotate" || dragging?.type === "pan2d" ? "grabbing" : mode === "pan" ? "grab" : mode === "pin" ? "crosshair" : "crosshair";

  return (
    <div className="rounded-3xl border border-white/10 bg-black/50 p-4" data-testid="aoi-selector">
      {/* Controls */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] p-1">
          <ToggleBtn active={view === "globe"} onClick={() => setView("globe")} testid="view-globe">
            <Globe2 className="h-3.5 w-3.5" /> Globe
          </ToggleBtn>
          <ToggleBtn active={view === "2d"} onClick={() => setView("2d")} testid="view-2d">
            <MapIcon className="h-3.5 w-3.5" /> 2D Map
          </ToggleBtn>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] p-1">
          <ToggleBtn active={mode === "rectangle"} onClick={() => setMode("rectangle")} testid="mode-rectangle">
            <Square className="h-3.5 w-3.5" /> Rectangle
          </ToggleBtn>
          <ToggleBtn active={mode === "polygon"} onClick={() => setMode("polygon")} testid="mode-polygon">
            <Pentagon className="h-3.5 w-3.5" /> Polygon
          </ToggleBtn>
          <ToggleBtn active={mode === "pin"} onClick={() => setMode("pin")} testid="mode-pin">
            <PinIcon className="h-3.5 w-3.5" /> Pin
          </ToggleBtn>
          {view === "globe" && (
            <ToggleBtn active={mode === "pan"} onClick={() => setMode("pan")} testid="mode-pan">
              <Hand className="h-3.5 w-3.5" /> Rotate
            </ToggleBtn>
          )}
          {view === "2d" && (
            <ToggleBtn active={mode === "pan"} onClick={() => setMode("pan")} testid="mode-pan">
              <Hand className="h-3.5 w-3.5" /> Pan
            </ToggleBtn>
          )}
          {mode === "polygon" && draftPoints.length >= 3 && (
            <button
              type="button"
              onClick={finishPolygon}
              data-testid="finish-polygon"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-medium text-black"
            >
              <Plus className="h-3.5 w-3.5" /> Add AOI
            </button>
          )}
          <button
            type="button"
            onClick={clearAll}
            data-testid="aoi-clear-all"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium text-slate-300 transition-colors hover:text-arjuna-teal"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Clear All
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#02070E]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full"
          style={{ cursor: cursorStyle, touchAction: "none" }}
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={handleUp}
          onPointerLeave={handleUp}
          onClick={handleClick}
          onWheel={handleWheel}
          data-testid="aoi-canvas"
        >
          <defs>
            <radialGradient id="globe-fill" cx="38%" cy="35%" r="75%">
              <stop offset="0%" stopColor="#0E2436" />
              <stop offset="65%" stopColor="#061322" />
              <stop offset="100%" stopColor="#020810" />
            </radialGradient>
          </defs>

          {view === "globe" && sphere && (
            <path d={sphere} fill="url(#globe-fill)" stroke="rgba(0,229,255,0.35)" strokeWidth="1" />
          )}
          {view === "2d" && <rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="#02070E" />}

          <path d={grat} fill="none" stroke="rgba(0,229,255,0.08)" strokeWidth="0.6" />
          <path d={landPath} fill="rgba(0,229,255,0.07)" stroke="rgba(0,229,255,0.4)" strokeWidth="0.8" />

          {/* Existing committed AOIs */}
          {aois.map((a, i) => {
            const color = AOI_COLORS[i % AOI_COLORS.length];
            const d = path({ type: "Polygon", coordinates: [a.coords] });
            if (!d) return null;
            return (
              <g key={a.id}>
                <path d={d} fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.6" />
                {(() => {
                  const c = path.centroid({ type: "Polygon", coordinates: [a.coords] });
                  if (!c || !isFinite(c[0])) return null;
                  return (
                    <g>
                      <circle cx={c[0]} cy={c[1]} r="14" fill="#02070E" stroke={color} strokeWidth="1.5" />
                      <text x={c[0]} y={c[1] + 5} textAnchor="middle" fill={color} fontSize="14" fontFamily="ui-monospace, monospace" fontWeight="700">
                        {i + 1}
                      </text>
                    </g>
                  );
                })()}
              </g>
            );
          })}

          {/* Rectangle preview while dragging */}
          {previewPath && (
            <path d={previewPath} fill="rgba(0,229,255,0.2)" stroke="#00E5FF" strokeWidth="1.4" strokeDasharray="6 6" />
          )}

          {/* Polygon draft */}
          {draftPath && <path d={draftPath} fill="none" stroke="#00E5FF" strokeWidth="1.6" strokeDasharray="4 4" />}
          {mode === "polygon" &&
            draftPoints.map(([lon, lat], i) => {
              const p = projection([lon, lat]);
              if (!p || !isFinite(p[0])) return null;
              return <circle key={i} cx={p[0]} cy={p[1]} r="4" fill="#00E5FF" />;
            })}

          {/* Pin from address search */}
          {pin && (() => {
            const p = projection([pin.lon, pin.lat]);
            if (!p || !isFinite(p[0])) return null;
            return (
              <g data-testid="aoi-pin">
                <circle cx={p[0]} cy={p[1]} r="16" fill="none" stroke="#FF7A1A" strokeWidth="1.2" opacity="0.6">
                  <animate attributeName="r" values="12;26;12" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0;0.8" dur="2.4s" repeatCount="indefinite" />
                </circle>
                <path
                  d={`M ${p[0]} ${p[1] - 18} C ${p[0] - 8} ${p[1] - 18}, ${p[0] - 8} ${p[1] - 6}, ${p[0]} ${p[1] + 2} C ${p[0] + 8} ${p[1] - 6}, ${p[0] + 8} ${p[1] - 18}, ${p[0]} ${p[1] - 18} Z`}
                  fill="#FF7A1A"
                  stroke="#02070E"
                  strokeWidth="1.2"
                />
                <circle cx={p[0]} cy={p[1] - 12} r="3" fill="#02070E" />
              </g>
            );
          })()}
        </svg>

        <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-arjuna-teal animate-pulse" />
          {hoverLL ? `${hoverLL[1].toFixed(3)}°, ${hoverLL[0].toFixed(3)}°` : "Hover to inspect"}
        </div>
        <div className="pointer-events-none absolute right-3 top-3 rounded-full border border-white/10 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 backdrop-blur">
          {view === "globe" ? "Orthographic · Live" : "Mercator · 2D"} · {aois.length} AOI{aois.length === 1 ? "" : "s"} · {zoom.toFixed(1)}×
        </div>

        {/* Zoom controls */}
        <div className="absolute right-3 bottom-3 flex flex-col gap-1 rounded-full border border-white/10 bg-black/60 p-1 backdrop-blur">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(40, z * 1.4))}
            data-testid="zoom-in"
            aria-label="Zoom in"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-200 transition-colors hover:bg-white/10 hover:text-arjuna-teal"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(0.4, z / 1.4))}
            data-testid="zoom-out"
            aria-label="Zoom out"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-200 transition-colors hover:bg-white/10 hover:text-arjuna-teal"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={resetView}
            data-testid="zoom-reset"
            aria-label="Reset view"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-200 transition-colors hover:bg-white/10 hover:text-arjuna-teal"
          >
            <Crosshair className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* AOI list */}
      {aois.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="aoi-list">
          {aois.map((a, i) => {
            const color = AOI_COLORS[i % AOI_COLORS.length];
            return (
              <div
                key={a.id}
                className="inline-flex items-center gap-2 rounded-full border bg-white/[0.03] py-1 pl-2 pr-1 text-xs"
                style={{ borderColor: `${color}55` }}
                data-testid={`aoi-chip-${i}`}
              >
                <span
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full font-mono text-[10px] font-bold text-black"
                  style={{ background: color }}
                >
                  {i + 1}
                </span>
                <span className="text-slate-200">
                  {a.mode === "polygon" ? "Polygon" : "Rectangle"} · {a.area_km2.toLocaleString("en-IN", { maximumFractionDigits: 0 })} km²
                </span>
                <button
                  type="button"
                  aria-label="Remove AOI"
                  onClick={() => removeAoi(a.id)}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-xs text-slate-400">
        {mode === "rectangle" && "Click & drag to add a rectangular AOI · Scroll to zoom · Switch to Pan to move."}
        {mode === "polygon" && "Click to add vertices (≥3). Press 'Add AOI' to save · Scroll to zoom · Pan to move."}
        {mode === "pin" && "Click anywhere on the map to drop a pin. We'll fetch the place name and center the view."}
        {mode === "pan" && (view === "globe"
          ? "Drag to rotate the globe · Scroll to zoom in on smaller regions."
          : "Drag to pan the map · Scroll to zoom. Switch back to Rectangle or Polygon to draw.")}
      </p>
    </div>
  );
}

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
        active ? "bg-arjuna-teal text-black" : "text-slate-300 hover:text-arjuna-teal"
      }`}
    >
      {children}
    </button>
  );
}
